import type { HintId } from '../components/portal-hints/hint-films';
import type { FilmRef } from '../components/academy/films/lesson-films';
import type { DoneCheckId } from './academy-done-checks';

/**
 * Academy-Katalog — die zentrale Inhaltsbasis von Academy und Portalhinweisen.
 *
 * Hier steht die STRUKTUR: feste IDs, Reihenfolge, Video-Quelle, verknüpfter
 * Portalhinweis, Ziel im Portal („Im Portal ausprobieren“), Lösungen der
 * Quizfragen und die Zertifikate. Die TEXTE (Titel, Zusammenfassung,
 * Kernpunkte, Fragen) stehen je Sprache in `src/assets/academy/<lang>.json`
 * unter derselben ID — eine spätere KI-Generierung schreibt nur diese Dateien
 * und das `video` einer Lektion, nichts sonst.
 *
 * Regeln:
 *  - IDs sind Identität: der Fortschritt hängt an ihnen (al-tutor
 *    `academy_progress.item_id` = `lesson:<id>` / `exam:<id>`). Nie
 *    umbenennen; eine ersetzte Lektion bekommt eine neue ID.
 *  - Ein Portalhinweis gehört zu genau einer Lektion (`hintId`), der Film ist
 *    derselbe (`hint-films.ts`) — Hinweis und Lektion werden nie doppelt
 *    gepflegt.
 *  - `version` hochzählen, wenn sich der Inhalt einer Lektion wesentlich
 *    ändert; der Fortschritt merkt sich, welche Fassung abgeschlossen wurde.
 *
 * Technische Doku: features/docs/technical/academy.md.
 */

export type AcademyTopicId =
  | 'getting-started'
  | 'agents'
  | 'knowledge'
  | 'widget'
  | 'lecturers'
  | 'analytics'
  | 'administration'
  | 'expert'
  | 'creator';

export type AcademyCertificateId = 'essentials' | 'professional' | 'expert';

/** Woher das Video einer Lektion kommt. */
export type AcademyVideo =
  /** Erklärfilm des verknüpften Portalhinweises (code-animiert, Loop). */
  | { kind: 'hint'; hintId: HintId }
  /** Videodatei (z. B. später KI-generiert); `src` absolut oder unter assets/. */
  | { kind: 'file'; src: string; poster?: string; generatedAt?: string }
  /** Film aus Szenen (`app-academy-film`), Untertitel je Szene aus
   *  `lessons.<id>.scenes`. Eine Szene = kurze Vorschau im Loop — derselbe
   *  Film taugt als Portalhinweis (Marcel, 29.10.2026: Vorschau zeigen,
   *  gelernt wird im Klickpfad). Mehrere Szenen laufen einmal durch. */
  | { kind: 'scenes'; scenes: FilmRef[] }
  /** Noch kein Video — Platzhalter mit Orbi. */
  | { kind: 'placeholder' };

/** Wohin „Im Portal ausprobieren“ führt. `anchor` = Katalog-Anker
 *  (`portal-ui-catalog.json`), den der Spotlight nach der Navigation zeigt. */
export interface AcademyTarget {
  route: string;
  queryParams?: Record<string, string>;
  anchor?: string;
}

/** Eine Quizfrage: Text in der Inhaltsdatei, hier nur die Lösung. */
export interface AcademyQuizKey {
  id: string;
  /** Index der richtigen Antwort in `options` der Inhaltsdatei. */
  correct: number;
}

export interface AcademyLesson {
  id: string;
  topic: AcademyTopicId;
  /** Ungefähre Dauer der ganzen Lektion in Minuten. */
  minutes: number;
  /** Länge des Erklärvideos in Sekunden (Ziel 30–90). */
  videoSeconds: number;
  video: AcademyVideo;
  /** Verknüpfter Portalhinweis (`HintDef.id`). */
  hintId?: HintId;
  target?: AcademyTarget;
  quiz?: AcademyQuizKey[];
  /** Lektionen, auf denen diese aufbaut (PRD academy-content › Didaktik 2). */
  requires?: string[];
  /** Bloom-Stufe des Lernziels. */
  bloom?: 'understand' | 'apply' | 'analyze' | 'evaluate';
  /** Geführter Klickpfad: Tour-ID im Katalog (`portal-ui-catalog.json` →
   *  `tours`), Texte je Schritt in `lessons.<id>.tour`. */
  tour?: string;
  /** Prüfung am echten Zustand — bestanden = Lektion abgeschlossen. */
  doneCheck?: DoneCheckId;
  /** Nur sichtbar, wenn die Funktion für diese Person an ist — sonst fehlt die
   *  Lektion überall (Thema, Suche, Zähler, Zertifikat). `portal-agent`: der
   *  Portal-Assistent (`AgentChatService.enabled`); `creator`: der Creator
   *  (wie der Menüpunkt — Funktion `CONTENT_GENERATOR` der Organisation und
   *  Zugriff der Person). */
  when?: 'portal-agent' | 'creator';
  version: number;
  /** Seit wann es die Lektion gibt (ISO-Datum) — „Neu“ für 30 Tage. */
  addedAt: string;
}

export interface AcademyTopic {
  id: AcademyTopicId;
  /** Eva-Icon oder Portal-Symbol (`pack: 'al'`). */
  icon: string;
  iconPack?: 'al';
}

export interface AcademyCertificate {
  id: AcademyCertificateId;
  /** Themen, deren Lektionen vor dem Abschlusstest abgeschlossen sein müssen. */
  topics: AcademyTopicId[];
  /** Zertifikat, das vorher bestanden sein muss. */
  requires?: AcademyCertificateId;
  /** Themen, aus deren Quizfragen der Abschlusstest zieht (Standard: `topics`). */
  examTopics?: AcademyTopicId[];
  /** Fragen je Abschlusstest. */
  examSize: number;
  /** Mindestanteil richtiger Antworten in Prozent. */
  passPercent: number;
}

/** „Was ist neu?“ — Text in der Inhaltsdatei (`news.<id>`). */
export interface AcademyNews {
  id: string;
  date: string;
  lessonId?: string;
}

export const ACADEMY_TOPICS: AcademyTopic[] = [
  { id: 'getting-started', icon: 'compass-outline' },
  { id: 'agents', icon: 'orbi', iconPack: 'al' },
  { id: 'knowledge', icon: 'knowledge', iconPack: 'al' },
  { id: 'widget', icon: 'chatStyling', iconPack: 'al' },
  { id: 'lecturers', icon: 'people-outline' },
  { id: 'analytics', icon: 'activity-outline' },
  { id: 'administration', icon: 'shield-outline' },
  { id: 'expert', icon: 'award-outline' },
  // Nur mit Creator sichtbar (alle Lektionen `when: 'creator'`); in keinem Zertifikat.
  { id: 'creator', icon: 'magicWand', iconPack: 'al' },
];

const ADDED = '2026-10-29';

export const ACADEMY_LESSONS: AcademyLesson[] = [
  // ── 1 Erste Schritte & Portalnavigation ──────────────────────────────
  // Muster (PRD academy-content): kurze Vorschau (= möglicher Portalhinweis)
  // → Klickpfad im echten Portal → Prüfung am echten Stand / Quiz.
  {
    id: 'portal-tour', topic: 'getting-started', minutes: 3, videoSeconds: 10,
    video: { kind: 'scenes', scenes: ['scene:portal-tour'] }, target: { route: '/knowledge-base' },
    quiz: [{ id: 'q1', correct: 1 }], requires: [], bloom: 'understand',
    tour: 'academy.portal-tour', version: 2, addedAt: ADDED,
  },
  {
    id: 'portal-assistant', topic: 'getting-started', minutes: 3, videoSeconds: 8,
    video: { kind: 'scenes', scenes: ['hint:meet-orbi'] }, hintId: 'meet-orbi', target: { route: '/tutors' },
    quiz: [{ id: 'q1', correct: 2 }], requires: ['portal-tour'], bloom: 'apply',
    tour: 'academy.portal-assistant', doneCheck: 'orbi-used', when: 'portal-agent', version: 2, addedAt: ADDED,
  },
  {
    id: 'hints-and-help', topic: 'getting-started', minutes: 2, videoSeconds: 11,
    video: { kind: 'scenes', scenes: ['scene:hints-and-help'] }, target: { route: '/academy', queryParams: { view: 'hints' } },
    quiz: [{ id: 'q1', correct: 0 }], requires: ['portal-tour'], bloom: 'understand',
    tour: 'academy.hints-and-help', version: 2, addedAt: ADDED,
  },

  // ── 2 KI-Tutoren erstellen & konfigurieren ───────────────────────────
  {
    id: 'create-agent', topic: 'agents', minutes: 3, videoSeconds: 8,
    video: { kind: 'scenes', scenes: ['hint:create-agent'] }, hintId: 'create-agent', target: { route: '/tutors' },
    quiz: [{ id: 'q1', correct: 1 }], requires: ['portal-tour'], bloom: 'apply',
    tour: 'academy.create-agent', doneCheck: 'has-agent', version: 2, addedAt: ADDED,
  },
  {
    id: 'agent-templates', topic: 'agents', minutes: 3, videoSeconds: 11,
    video: { kind: 'scenes', scenes: ['scene:agent-templates'] }, target: { route: '/tutors' },
    quiz: [{ id: 'q1', correct: 0 }, { id: 'q2', correct: 2 }], requires: ['create-agent'], bloom: 'understand',
    tour: 'academy.agent-templates', version: 2, addedAt: ADDED,
  },
  {
    id: 'ai-configuration', topic: 'agents', minutes: 4, videoSeconds: 12,
    video: { kind: 'scenes', scenes: ['scene:ai-configuration'] }, target: { route: '/tutors' },
    quiz: [{ id: 'q1', correct: 2 }], requires: ['create-agent'], bloom: 'apply',
    tour: 'academy.ai-configuration', version: 2, addedAt: ADDED,
  },
  {
    id: 'try-agent', topic: 'agents', minutes: 2, videoSeconds: 8,
    video: { kind: 'scenes', scenes: ['hint:try-agent'] }, hintId: 'try-agent', target: { route: '/tutors' },
    quiz: [{ id: 'q1', correct: 1 }], requires: ['create-agent'], bloom: 'apply',
    tour: 'academy.try-agent', doneCheck: 'preview-tried', version: 2, addedAt: ADDED,
  },

  // ── 3 Wissensbasis & Synchronisierung ────────────────────────────────
  // Pilot (PRD academy-content › 4): Warum → kurze Vorschau (= Hinweis-Film) → Klickpfad → Prüfen.
  {
    id: 'create-knowledge-base', topic: 'knowledge', minutes: 4, videoSeconds: 8,
    video: { kind: 'scenes', scenes: ['hint:create-knowledge-base'] },
    hintId: 'create-knowledge-base',
    target: { route: '/knowledge-base', anchor: 'knowledge.create-bucket' },
    quiz: [{ id: 'q1', correct: 0 }], requires: ['create-agent'], bloom: 'apply',
    tour: 'academy.create-knowledge-base', doneCheck: 'has-folder', version: 2, addedAt: ADDED,
  },
  {
    id: 'link-knowledge-base', topic: 'knowledge', minutes: 4, videoSeconds: 8,
    video: { kind: 'scenes', scenes: ['hint:link-knowledge-base'] },
    hintId: 'link-knowledge-base',
    target: { route: '/tutors' },
    quiz: [{ id: 'q1', correct: 0 }], requires: ['create-agent', 'create-knowledge-base'], bloom: 'apply',
    tour: 'academy.link-knowledge-base', doneCheck: 'agents-have-knowledge', version: 2, addedAt: ADDED,
  },
  {
    id: 'knowledge-mode', topic: 'knowledge', minutes: 4, videoSeconds: 8,
    video: { kind: 'scenes', scenes: ['hint:limit-knowledge'] },
    hintId: 'limit-knowledge',
    target: { route: '/tutors' },
    quiz: [{ id: 'q1', correct: 0 }, { id: 'q2', correct: 0 }], requires: ['link-knowledge-base'], bloom: 'evaluate',
    tour: 'academy.knowledge-mode', doneCheck: 'knowledge-mode-set', version: 2, addedAt: ADDED,
  },
  {
    id: 'course-sync', topic: 'knowledge', minutes: 4, videoSeconds: 12,
    video: { kind: 'scenes', scenes: ['scene:sync-toggle'] },
    target: { route: '/tutors/placements' },
    quiz: [{ id: 'q1', correct: 0 }], requires: ['link-knowledge-base', 'connect-lms'], bloom: 'apply',
    tour: 'academy.course-sync', doneCheck: 'course-sync-on', version: 2, addedAt: ADDED,
  },

  // ── 4 Widget & Lerner-Interaktion ────────────────────────────────────
  {
    id: 'widget-interface', topic: 'widget', minutes: 3, videoSeconds: 10,
    video: { kind: 'scenes', scenes: ['scene:widget-interface'] }, target: { route: '/chat-styling' },
    quiz: [{ id: 'q1', correct: 0 }], requires: ['create-agent'], bloom: 'evaluate',
    tour: 'academy.widget-interface', version: 2, addedAt: ADDED,
  },
  {
    id: 'widget-styling', topic: 'widget', minutes: 2, videoSeconds: 8,
    video: { kind: 'scenes', scenes: ['hint:style-chat'] }, hintId: 'style-chat', target: { route: '/chat-styling' },
    quiz: [{ id: 'q1', correct: 2 }], requires: ['widget-interface'], bloom: 'apply',
    tour: 'academy.widget-styling', version: 2, addedAt: ADDED,
  },
  {
    id: 'learning-aids', topic: 'widget', minutes: 3, videoSeconds: 11,
    video: { kind: 'scenes', scenes: ['scene:learning-aids'] }, target: { route: '/tutors' },
    quiz: [{ id: 'q1', correct: 1 }], requires: ['ai-configuration'], bloom: 'understand',
    tour: 'academy.learning-aids', version: 2, addedAt: ADDED,
  },
  {
    id: 'onboarding-tour', topic: 'widget', minutes: 2, videoSeconds: 11,
    video: { kind: 'scenes', scenes: ['scene:onboarding-tour'] }, target: { route: '/tutors' },
    quiz: [{ id: 'q1', correct: 0 }], requires: ['create-agent'], bloom: 'apply',
    tour: 'academy.onboarding-tour', version: 2, addedAt: ADDED,
  },

  // ── 5 Lehrende, Alpha Twin & Human-in-the-Loop ───────────────────────
  {
    id: 'alpha-twin', topic: 'lecturers', minutes: 3, videoSeconds: 11,
    video: { kind: 'scenes', scenes: ['scene:alpha-twin'] }, target: { route: '/tutors' },
    quiz: [{ id: 'q1', correct: 1 }], requires: ['create-agent'], bloom: 'apply',
    tour: 'academy.alpha-twin', doneCheck: 'has-twin', version: 2, addedAt: ADDED,
  },
  {
    id: 'human-in-the-loop', topic: 'lecturers', minutes: 4, videoSeconds: 16,
    video: { kind: 'scenes', scenes: ['scene:human-in-the-loop'] }, target: { route: '/dozent/messages' },
    quiz: [{ id: 'q1', correct: 2 }, { id: 'q2', correct: 1 }], requires: ['alpha-twin'], bloom: 'apply',
    tour: 'academy.human-in-the-loop', version: 2, addedAt: ADDED,
  },
  {
    id: 'lecturer-exercises', topic: 'lecturers', minutes: 3, videoSeconds: 11,
    video: { kind: 'scenes', scenes: ['scene:lecturer-exercises'] }, target: { route: '/dozent/exercises' },
    quiz: [{ id: 'q1', correct: 0 }], requires: ['alpha-twin'], bloom: 'apply',
    tour: 'academy.lecturer-exercises', version: 2, addedAt: ADDED,
  },
  {
    id: 'lecturer-faqs', topic: 'lecturers', minutes: 2, videoSeconds: 11,
    video: { kind: 'scenes', scenes: ['scene:lecturer-faqs'] }, target: { route: '/dozent/faqs' },
    quiz: [{ id: 'q1', correct: 1 }], requires: ['alpha-twin'], bloom: 'apply',
    tour: 'academy.lecturer-faqs', version: 2, addedAt: ADDED,
  },

  // ── 6 Analytics & Learner Profiles ───────────────────────────────────
  {
    id: 'analytics-overview', topic: 'analytics', minutes: 3, videoSeconds: 11,
    video: { kind: 'scenes', scenes: ['scene:analytics-overview'] }, target: { route: '/analytics/live' },
    quiz: [{ id: 'q1', correct: 1 }], requires: ['try-agent'], bloom: 'understand',
    tour: 'academy.analytics-overview', version: 2, addedAt: ADDED,
  },
  {
    id: 'conversation-insights', topic: 'analytics', minutes: 3, videoSeconds: 8,
    video: { kind: 'scenes', scenes: ['hint:review-conversations'] }, hintId: 'review-conversations', target: { route: '/analytics/conversations' },
    quiz: [{ id: 'q1', correct: 0 }], requires: ['analytics-overview'], bloom: 'analyze',
    tour: 'academy.conversation-insights', version: 2, addedAt: ADDED,
  },
  {
    id: 'learner-profiles', topic: 'analytics', minutes: 4, videoSeconds: 11,
    video: { kind: 'scenes', scenes: ['scene:learner-profiles'] }, target: { route: '/dozent/participants' },
    quiz: [{ id: 'q1', correct: 2 }, { id: 'q2', correct: 1 }], requires: ['analytics-overview'], bloom: 'analyze',
    tour: 'academy.learner-profiles', version: 2, addedAt: ADDED,
  },

  // ── 7 Administration, Integrationen & Datenschutz ────────────────────
  {
    id: 'connect-lms', topic: 'administration', minutes: 3, videoSeconds: 8,
    video: { kind: 'scenes', scenes: ['hint:connect-lms'] }, hintId: 'connect-lms', target: { route: '/integrations' },
    quiz: [{ id: 'q1', correct: 1 }], requires: ['create-agent'], bloom: 'apply',
    tour: 'academy.connect-lms', doneCheck: 'lms-connected', version: 2, addedAt: ADDED,
  },
  {
    id: 'place-agents', topic: 'administration', minutes: 3, videoSeconds: 8,
    video: { kind: 'scenes', scenes: ['hint:place-agents'] }, hintId: 'place-agents', target: { route: '/tutors/placements' },
    quiz: [{ id: 'q1', correct: 0 }], requires: ['connect-lms'], bloom: 'apply',
    tour: 'academy.place-agents', doneCheck: 'agents-placed', version: 2, addedAt: ADDED,
  },
  {
    id: 'users-and-roles', topic: 'administration', minutes: 2, videoSeconds: 8,
    video: { kind: 'scenes', scenes: ['hint:invite-team'] }, hintId: 'invite-team', target: { route: '/organisation' },
    quiz: [{ id: 'q1', correct: 2 }], requires: ['portal-tour'], bloom: 'apply',
    tour: 'academy.users-and-roles', version: 2, addedAt: ADDED,
  },
  {
    id: 'credits-and-plans', topic: 'administration', minutes: 2, videoSeconds: 8,
    video: { kind: 'scenes', scenes: ['hint:credits-trial'] }, hintId: 'credits-trial', target: { route: '/products' },
    quiz: [{ id: 'q1', correct: 1 }], requires: ['try-agent'], bloom: 'understand',
    tour: 'academy.credits-and-plans', version: 2, addedAt: ADDED,
  },
  {
    id: 'privacy', topic: 'administration', minutes: 3, videoSeconds: 11,
    video: { kind: 'scenes', scenes: ['scene:privacy'] }, target: { route: '/tutors' },
    quiz: [{ id: 'q1', correct: 0 }], requires: ['onboarding-tour'], bloom: 'understand',
    tour: 'academy.privacy', version: 2, addedAt: ADDED,
  },

  // ── 8 Expert Academy & Best Practices ────────────────────────────────
  {
    id: 'prompt-craft', topic: 'expert', minutes: 4, videoSeconds: 12,
    video: { kind: 'scenes', scenes: ['scene:prompt-craft'] }, target: { route: '/tutors' },
    quiz: [{ id: 'q1', correct: 1 }], requires: ['ai-configuration'], bloom: 'evaluate',
    tour: 'academy.prompt-craft', version: 2, addedAt: ADDED,
  },
  {
    id: 'knowledge-curation', topic: 'expert', minutes: 4, videoSeconds: 11,
    video: { kind: 'scenes', scenes: ['scene:knowledge-curation'] }, target: { route: '/knowledge-base' },
    quiz: [{ id: 'q1', correct: 2 }], requires: ['create-knowledge-base'], bloom: 'evaluate',
    tour: 'academy.knowledge-curation', version: 2, addedAt: ADDED,
  },
  {
    id: 'agent-learnings', topic: 'expert', minutes: 3, videoSeconds: 11,
    video: { kind: 'scenes', scenes: ['scene:agent-learnings'] }, target: { route: '/agent-setup' },
    quiz: [{ id: 'q1', correct: 0 }], requires: ['ai-configuration'], bloom: 'apply',
    tour: 'academy.agent-learnings', version: 2, addedAt: ADDED,
  },
  {
    id: 'course-rollout', topic: 'expert', minutes: 5, videoSeconds: 16,
    video: { kind: 'scenes', scenes: ['scene:course-rollout'] }, target: { route: '/analytics/conversations' },
    quiz: [{ id: 'q1', correct: 1 }, { id: 'q2', correct: 1 }], requires: ['place-agents'], bloom: 'evaluate',
    tour: 'academy.course-rollout', version: 2, addedAt: ADDED,
  },

  // ── 9 Creator (nur mit Creator) ──────────────────────────────────────
  // Der Kurs-Editor läuft in einem eingebetteten Rahmen — die Klickpfade
  // führen bis zu ihm; was darin geschieht, zeigen Film und Text.
  {
    id: 'creator-overview', topic: 'creator', minutes: 3, videoSeconds: 12,
    video: { kind: 'scenes', scenes: ['scene:creator-overview'] }, target: { route: '/creator' },
    quiz: [{ id: 'q1', correct: 1 }, { id: 'q2', correct: 0 }], requires: ['portal-tour'], bloom: 'understand',
    tour: 'academy.creator-overview', when: 'creator', version: 1, addedAt: '2026-11-04',
  },
  {
    id: 'creator-import', topic: 'creator', minutes: 4, videoSeconds: 13,
    video: { kind: 'scenes', scenes: ['scene:creator-import'] }, target: { route: '/creator', anchor: 'creator.import' },
    quiz: [{ id: 'q1', correct: 1 }, { id: 'q2', correct: 1 }], requires: ['creator-overview'], bloom: 'apply',
    tour: 'academy.creator-import', when: 'creator', version: 1, addedAt: '2026-11-04',
  },
  {
    id: 'creator-design', topic: 'creator', minutes: 5, videoSeconds: 14,
    video: { kind: 'scenes', scenes: ['scene:creator-design'] }, target: { route: '/creator' },
    quiz: [{ id: 'q1', correct: 1 }, { id: 'q2', correct: 0 }], requires: ['creator-overview'], bloom: 'apply',
    tour: 'academy.creator-design', when: 'creator', version: 1, addedAt: '2026-11-04',
  },
  {
    id: 'creator-review', topic: 'creator', minutes: 5, videoSeconds: 16,
    video: { kind: 'scenes', scenes: ['scene:creator-review'] }, target: { route: '/creator/library' },
    quiz: [{ id: 'q1', correct: 1 }, { id: 'q2', correct: 1 }, { id: 'q3', correct: 1 }], requires: ['creator-design'], bloom: 'apply',
    tour: 'academy.creator-review', when: 'creator', version: 1, addedAt: '2026-11-04',
  },
];

export const ACADEMY_CERTIFICATES: AcademyCertificate[] = [
  { id: 'essentials', topics: ['getting-started', 'agents', 'knowledge', 'widget'], examSize: 10, passPercent: 80 },
  {
    id: 'professional', topics: ['lecturers', 'analytics', 'administration'], requires: 'essentials',
    examSize: 10, passPercent: 80,
  },
  {
    id: 'expert', topics: ['expert'], requires: 'professional',
    examTopics: ['getting-started', 'agents', 'knowledge', 'widget', 'lecturers', 'analytics', 'administration', 'expert'],
    examSize: 12, passPercent: 80,
  },
];

export const ACADEMY_NEWS: AcademyNews[] = [
  { id: 'academy-launch', date: '2026-10-29', lessonId: 'hints-and-help' },
  { id: 'liquid-interface', date: '2026-10-27', lessonId: 'widget-interface' },
  { id: 'competency-profile', date: '2026-10-27', lessonId: 'learner-profiles' },
];

/** „Neu“-Markierung einer Lektion: so viele Tage ab `addedAt`. */
export const NEW_LESSON_DAYS = 30;

export function lessonById(id: string): AcademyLesson | undefined {
  return ACADEMY_LESSONS.find((l) => l.id === id);
}

export function lessonsOfTopic(topic: AcademyTopicId): AcademyLesson[] {
  return ACADEMY_LESSONS.filter((l) => l.topic === topic);
}

/** Die Academy-Lektion zu einem Portalhinweis (Verknüpfung Hinweis → Lektion). */
export function lessonForHint(hintId: string): AcademyLesson | undefined {
  return ACADEMY_LESSONS.find((l) => l.hintId === hintId);
}

export function certificateById(id: string): AcademyCertificate | undefined {
  return ACADEMY_CERTIFICATES.find((c) => c.id === id);
}

/** Zertifikat, zu dem ein Thema gehört. */
export function certificateOfTopic(topic: AcademyTopicId): AcademyCertificate | undefined {
  return ACADEMY_CERTIFICATES.find((c) => c.topics.includes(topic));
}
